/**
 * SQL fragments for the audits list — transcribed verbatim from the legacy
 * `audits-service/db/queries/audits.js#list` so a reviewer can diff the two
 * side by side. Only these fragments are factored out of `list.ts`.
 */

import { buildCompaniesConnectedSql } from '../../../../lib/company-links.js';

/**
 * "Public means public to everyone the audited company is connected to."
 *
 * A public audit is readable by the viewer as a **rule evaluated at read
 * time**, not as a row someone remembered to write. The grant used to be
 * materialised instead: creating a public audit fanned a READ row out to every
 * company linked to the uploader *that day*. A network written by three
 * different paths and edited forever after cannot be tracked that way — on the
 * 2026-09-20 production clone 14,524 of 37,418 (public audit, connected
 * company) pairs had no row, 92% of them companies that connected after the
 * upload, and flipping an audit to public later granted nothing at all
 * [DELTA-2545].
 *
 * Anchored on `company_id` — the **audited** company, the one whose profile
 * the audit describes — rather than the uploader, whose network was an
 * artefact of how the fan-out happened to be written.
 *
 * If audit access ever moves to IAM, a materialised READ grant may have to
 * come back; the fan-out that wrote one is in this file's git history
 * (`grantAccessForCreatedAudit`, `sharePublicAssets`).
 *
 * @param auditAlias the table/alias holding `is_public` and `company_id`
 * @param viewerCompanyIdPlaceholder bound placeholder for the viewer's company
 */
export const buildPublicToConnectedSql = (
  auditAlias: string,
  viewerCompanyIdPlaceholder: string,
): string =>
  `(${auditAlias}.is_public = 1 AND ${buildCompaniesConnectedSql(
    viewerCompanyIdPlaceholder,
    `${auditAlias}.company_id`,
  )})`;

/**
 * The audited company's name, in priority order:
 *   1-2. the viewer's own ABOUT profile of that company — brands rename their
 *        suppliers, so their label wins when they have one.
 *   3.   `audits_service_audits.company_name`, denormalised when the audit is
 *        created/imported. This is what makes amfori-synced audits legible:
 *        they attach to companies the brand has no profile row for, so 1-2
 *        are NULL and the column rendered blank (DELTA-2424).
 *   4-5. the audited company's own ABOUT profile (`for = by = on_behalf`, the
 *        same shape `companies_view` reads), for rows predating the
 *        denormalised column.
 * Kept in one place so the SELECT and the `companyName` sort can never drift —
 * sorting on a different expression than the one displayed pushed every one
 * of these rows into a single NULL block.
 */
export const COMPANY_NAME_SORT_EXPRESSION = `COALESCE(
        company_profiles.data->'display_name'->>'value',
        company_profiles.data->'official_name'->>'value',
        audits_service_audits.company_name,
        audited_company_profile.data->'display_name'->>'value',
        audited_company_profile.data->'official_name'->>'value'
      )`;

/**
 * The `companyName` FILTER expression, ported verbatim — note it probes the
 * camelCase `displayName`/`officialName` keys (which profile data does not
 * use), so in practice it matches the denormalised
 * `audits_service_audits.company_name` or `''`. A real inconsistency in the
 * legacy builder, kept for parity.
 */
export const COMPANY_NAME_FILTER_EXPRESSION = `UPPER(TRIM(COALESCE(
      company_profiles.data->'displayName'->>'value',
      company_profiles.data->'officialName'->>'value',
      audits_service_audits.company_name,
      ''
    )))`;

/**
 * The full SELECT list, verbatim from the legacy query — except `rights`,
 * which now falls back to the public-to-connected rule.
 *
 * `rights` is the censoring switch: `audits-list.service.ts` blanks a row when
 * it is empty, and the frontend masks it behind "Request access". A row the
 * viewer sees only because the audit is public has no access-control row to
 * read rights from, so the rule supplies READ. It must be **jsonb** and not a
 * JSON string: `normalizeRights` splits a string on commas, which would turn
 * `'["READ"]'` into the rights `['["READ"]']`.
 *
 * `access_status` (below) deliberately gets no such fallback — it is the
 * viewer's own archive flag, and a public-by-rule row has no share to archive.
 *
 * @param publicToConnectedSql `buildPublicToConnectedSql` for the outer query
 */
export const buildAuditListSelectFields = (
  publicToConnectedSql: string,
): string => `
    audits_service_audits.id,
    audits_service_audits.updated_at,
    audits_service_audits.created_at,
    audits_service_audits.company_id,
    ${COMPANY_NAME_SORT_EXPRESSION} as company_name,
    audits_service_audits.name,
    audits_service_audits.audit_type_id,
    audit_types.name as audit_type_name,
    audits_service_audits.type,
    audits_service_audits.audited_by,
    audits_service_audits.audit_date,
    audits_service_audits.reference_number,
    audits_service_audits.facility_score,
    audits_service_audits.next_planned_audit_date,
    audits_service_audits.audit_report_file_id,
    audits_service_audits.external_source,
    audits_service_audits.generated_by,
    -- The Retraced Social Score of the audit's newest COMPLETED run.
    -- Pending and failed runs read as no score rather than as a number
    -- nothing stands behind, so the column only ever shows a real one.
    social_score.total_score AS social_score,
    digitized_audit.status AS digitization_status,
    -- Cast bigint -> int4 so the pg driver returns a JS number. int8 (OID 20)
    -- is returned as a string when the global type parser is bypassed (e.g.
    -- under dd-trace on staging); the digitizationPercentage response field is
    -- a number|null union, and a string would fail serialisation for any
    -- company with a digitized audit. [DELTA-2215]
    digitized_audit.percentage::int AS digitization_percentage,
    company_profiles.data->'supply_chain_tier'->'value' AS tier_level,
    company_profiles.data->'collaboration_status'->>'value' AS collaboration_status,
    company_profiles.data->'country'->>'value' AS country,
    company_profiles.data->'vendors'->'value' as vendors,
    company_profiles.data->'agents'->'value' as agents,
    company_profiles.data->'internal_company_code'->>'value' AS internal_company_code,
    company_profiles.data->'notes'->>'value' AS notes,
    company_profiles.data->'city'->>'value' AS city,
    audit_report_file.url AS audit_report_file_url,
    audit_report_file.file_type AS audit_report_file_type,
    audit_report_file.content_type AS audit_report_file_content_type,
    audit_report_file.bucket_name AS audit_report_file_bucket_name,
    audits_service_audits.corrective_action_plan_file_id,
    corrective_action_plan_file.url AS corrective_action_plan_file_url,
    corrective_action_plan_file.file_type AS corrective_action_plan_file_file_type,
    corrective_action_plan_file.content_type AS corrective_action_plan_file_content_type,
    corrective_action_plan_file.bucket_name AS corrective_action_plan_file_bucket_name,
    audits_service_audits.uploader_company_id,
    audits_service_audits.uploader_company_name,
    COUNT(audits_service_audits.id) OVER() as count,
    COALESCE(
      asset_access_control.rights,
      CASE WHEN ${publicToConnectedSql} THEN '["READ"]'::jsonb END
    ) as rights,
    audits_service_audits.is_public,
    audit_access_requests.status as request_status,
    (
      SELECT string_agg(fp_item ->> 'id', ',' ORDER BY fp_item ->> 'id')
      FROM jsonb_array_elements(
        CASE
          WHEN jsonb_typeof(COALESCE(company_profiles.data::jsonb, '{}'::jsonb) -> 'facility_processes' -> 'value') = 'array'
          THEN COALESCE(company_profiles.data::jsonb, '{}'::jsonb) -> 'facility_processes' -> 'value'
          ELSE '[]'::jsonb
        END
      ) AS fp_item
    ) AS request_company_facility_processes`;

/**
 * The columns only the public API reads, appended to the SELECT when
 * `includePublicColumns` is set (`list.ts`).
 *
 * They are opt-in rather than always-on because the internal list publishes
 * whatever this query returns: `transformAuditListRow` spreads `...raw` and
 * `AuditListRowSchema` is a `z.looseObject`, so an undeclared column rides
 * straight onto the `/api/audits` wire. Switching this on for the dashboard
 * would widen that response by columns nobody asked for.
 *
 * Two groups:
 *
 * - **Provenance.** `created_by_company_id`, the one actor column the row
 *   keeps. The other twelve come from `entity_history`, read for the page
 *   after this query (`withAuditActors` in `list.ts`).
 * - **`access_status`.** The only source of `isArchived`: archiving an audit
 *   is per-viewing-company and lives in the access-control row, not on the
 *   audit. NULL for the audited company, which owns its audits outright and
 *   has no access-control row — correctly reading as "not archived by you".
 */
export const AUDIT_LIST_PUBLIC_EXTRA_FIELDS = `,
    audits_service_audits.created_by_company_id,
    asset_access_control.status as access_status`;

export interface VersionRankJoinOpts {
  /** Placeholder for the AUDITS asset type ('AUDITS'). */
  auditAssetTypePlaceholder: string;
  /** Placeholder for the viewer's company id. */
  viewerCompanyIdPlaceholder: string;
  /**
   * Extra predicates on the subquery's `WHERE 1=1` (already prefixed with
   * `AND ...`) — the access-control requirement in shared mode, the
   * owner-or-shared disjunction for the public read, the audited-company
   * pin-down.
   */
  extraWhereClauses: string[];
}

/**
 * The `MAX_AUDIT` version-rank INNER JOIN, verbatim from the legacy query:
 * one visible row per `(company_id, audit_type_id)` — the version with the
 * furthest `next_planned_audit_date`, NOT the most recently uploaded, so an
 * older report lands in version history without displacing the current valid
 * audit. Ties (and rows without a next planned date) fall back to
 * `audit_date`, then upload time, then id.
 */
export const buildVersionRankJoin = (opts: VersionRankJoinOpts): string => `
    INNER JOIN (
        SELECT audits_versioning.id AS active_audit_id,
        ROW_NUMBER() OVER (
            PARTITION BY audits_versioning.company_id, audits_versioning.audit_type_id
            ORDER BY
              audits_versioning.next_planned_audit_date DESC NULLS LAST,
              audits_versioning.audit_date DESC NULLS LAST,
              audits_versioning.created_at DESC,
              audits_versioning.id DESC
        ) AS version_rank
        FROM audits_service_audits audits_versioning
        LEFT JOIN companies_service_asset_access_control asset_access_control_versioning
        ON asset_access_control_versioning.asset_id = audits_versioning.id
        AND asset_access_control_versioning.asset_type = ${opts.auditAssetTypePlaceholder}
        AND asset_access_control_versioning.shared_with_company_id = ${opts.viewerCompanyIdPlaceholder}
        WHERE 1=1
        -- Preserves the pre-ROW_NUMBER behaviour: the old join on
        -- audit_type_id (NULL = NULL is false) never matched audits without
        -- an audit type, so they stay invisible here too.
        AND audits_versioning.audit_type_id IS NOT NULL
        ${opts.extraWhereClauses.join('\n        ')}
    ) MAX_AUDIT
    ON audits_service_audits.id = MAX_AUDIT.active_audit_id
    AND MAX_AUDIT.version_rank = 1`;
